import type { ModelCapabilities, OrgLlmModel } from "@/shared/domain/orgs";

import { newModelId, withDefaultModel } from "./modelList";

/** A model on one of the org's own connections, as the model dialog edits it. */
export interface OwnModelDraft {
  connection_id: string | null;
  /** The provider's own model id, e.g. `claude-sonnet-5`. */
  model_id: string;
  label: string;
  description: string;
  /** Typed text until saved; empty uses the deployment default. */
  context_window_tokens: string;
  /** The model's own output limit, as typed; empty sets none. */
  max_output_tokens: string;
  /** Capability flags set by the admin; the rest default by model family. */
  capability_overrides: Partial<ModelCapabilities>;
  in_picker: boolean;
}

/** An empty draft on `connectionId`, offered in the chat picker. */
export function emptyOwnModelDraft(connectionId: string | null): OwnModelDraft {
  return {
    connection_id: connectionId,
    model_id: "",
    label: "",
    description: "",
    context_window_tokens: "",
    max_output_tokens: "",
    capability_overrides: {},
    in_picker: true,
  };
}

/** The draft an existing own model starts the edit dialog from. */
export function ownModelDraftOf(model: OrgLlmModel): OwnModelDraft {
  return {
    connection_id: model.connection_id ?? null,
    model_id: model.model_id,
    label: model.label,
    description: model.description,
    context_window_tokens:
      model.context_window_tokens === null
        ? ""
        : String(model.context_window_tokens),
    max_output_tokens:
      model.max_output_tokens === null || model.max_output_tokens === undefined
        ? ""
        : String(model.max_output_tokens),
    capability_overrides: model.capability_overrides ?? {},
    in_picker: model.in_picker !== false,
  };
}

/** Why the draft cannot be saved yet, or `null` when it can. */
export function ownModelDraftError(draft: OwnModelDraft): string | null {
  if (draft.connection_id === null) {
    return "Pick a connection.";
  }
  if (draft.model_id.trim() === "") {
    return "Enter the provider's model id.";
  }
  const window = draft.context_window_tokens.trim();
  if (window !== "" && !/^[1-9]\d*$/.test(window)) {
    return "The context window must be a positive whole number of tokens.";
  }
  const outputLimit = draft.max_output_tokens.trim();
  if (outputLimit !== "" && !/^[1-9]\d*$/.test(outputLimit)) {
    return "The output limit must be a positive whole number of tokens.";
  }
  if (draft.capability_overrides.tool_use === false) {
    return "Chat turns and compression call tools, so tool use can't be off.";
  }
  return null;
}

/**
 * The list with a draft {@link ownModelDraftError} accepts saved into it: in
 * place of the entry `editingId`, or appended under a profile id not yet on
 * the list. An edited model keeps its roles and whether it is enabled (the
 * table changes those); a new one is enabled with no role, except that a new
 * model in the chat picker becomes the default of a list without one.
 * `entry` is the saved-in model as it lands on the list.
 */
export function modelsWithOwnModel(
  models: readonly OrgLlmModel[],
  draft: OwnModelDraft,
  editingId?: string,
): { models: OrgLlmModel[]; entry: OrgLlmModel } {
  const modelId = draft.model_id.trim();
  const label = draft.label.trim() === "" ? modelId : draft.label.trim();
  const window = draft.context_window_tokens.trim();
  const outputLimit = draft.max_output_tokens.trim();
  const existing = models.find((m) => m.id === editingId);
  const id = existing?.id ?? newModelId(label, models);
  const fields: OrgLlmModel = {
    ...existing,
    id,
    label,
    description: draft.description.trim(),
    model_id: modelId,
    connection_id: draft.connection_id,
    is_default: existing?.is_default ?? false,
    is_utility: existing?.is_utility === true,
    in_picker: draft.in_picker,
    context_window_tokens: window === "" ? null : Number(window),
    max_output_tokens: outputLimit === "" ? null : Number(outputLimit),
    capability_overrides: draft.capability_overrides,
  };

  let next =
    existing === undefined
      ? [...models, fields]
      : models.map((m) => (m.id === id ? fields : m));
  if (
    existing === undefined &&
    draft.in_picker &&
    !models.some((m) => m.is_default)
  ) {
    next = withDefaultModel(next, id);
  }
  const entry = next.find((m) => m.id === id) ?? fields;
  return { models: next, entry };
}
